import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    Paper,
    SelectChangeEvent,
    Typography,
} from "@mui/material";
import RegionSelector from "src/components/region/RegionSelector";
import {fileUploadFields} from "src/constants/ExcelFields";
import SimpleFileInput from "src/components/input/SimpleFileInput";
import {getCarrierPresignedUrl, uploadFileToS3} from "src/services/CarrierAccountAPI";

const SelfServeCarrierAccountSafelisting: React.FC= () => {
    const [file, setFile] = useState<File | null>(null);
    const [region, setRegion] = useState<string>("");
    const [uploading, setUploading] = useState<boolean>(false);

    const handleRegionChange = (event: SelectChangeEvent) => {
        setRegion(event.target.value as string);
    };

    const handleFileChange = (files: FileList | null) => {
        const uploadedFile = files?.[0];
        setFile(uploadedFile ?? null);
    };

    const handleSubmit = async () => {
        if (!file || !region) return;

        setUploading(true);
        try {
            const response = await getCarrierPresignedUrl(file.name, region);

            if (!response?.url) {
                throw new Error('Failed to generate upload URL.');
            }

            await uploadFileToS3(response.url, file);
            alert("File uploaded successfully!");
        } catch (error: any) {
            alert(`Upload failed: ${error.message}`);
        } finally {
            setUploading(false);
        }
    };

    const generateXls = (e: React.MouseEvent) => {
        e.preventDefault();
        const xlsContent = fileUploadFields.join('\t');
        const blob = new Blob([xlsContent], { type: 'application/vnd.ms-excel' });
        const a = document.createElement('a');
        a.download = 'template.xls';
        a.href = window.URL.createObjectURL(blob);
        a.click();
        a.remove();
    };

    return (
        <Box sx={{ bgcolor: "#fff", minHeight: "100vh", py: 2 }}>
            <Container maxWidth="lg">
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h4" align="center" gutterBottom>
                        Self-Serve Carrier Account Safelisting
                    </Typography>

                    <Box
                        sx={{
                            mt: 3,
                            bgcolor: "#f5f5f5",
                            p: 4,
                            borderRadius: 2,
                            textAlign: "center",
                            width: "100%",
                            boxShadow: 2,
                        }}
                    >
                        <Typography variant="h6" gutterBottom align="center">
                            Instructions
                        </Typography>

                        <Typography variant="body1" paragraph align="left">
                            Please download the excel template, and upload it after filling in all the required fields:
                        </Typography>

                        <Typography variant="body1" paragraph align="left">
                            {fileUploadFields.map((field, i) => (
                                <span key={i}>
                                    {i + 1}. <strong>{field}</strong><br />
                                </span>
                            ))}
                        </Typography>

                        <Typography variant="body2" paragraph align="left">
                            Make sure all required fields are filled in before uploading and the file should not exceed 5 MB. Only files with the correct format will be accepted.
                        </Typography>
                        <Box>
                            <Button onClick={generateXls}>
                            Download Excel
                            </Button>
                        </Box>
                        <RegionSelector region={region} onChange={handleRegionChange} />

                        <Box mt={3} mb={2} >
                            <Button
                                variant="outlined"
                                component="label"
                                sx={{ width: 300, height: 46 }}
                                disabled={!region}
                            >
                                <SimpleFileInput
                                    inputRef={null}
                                    inputId="xls-upload"
                                    accept=".xls"
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </Box>

                        <Button
                            variant="contained"
                            disabled={!file}
                            sx={{ width: 300, height: 46 }}
                            onClick={handleSubmit}
                        >
                            {uploading ? "Uploading..." : "Submit"}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default SelfServeCarrierAccountSafelisting;
